import { useState, useEffect, useMemo } from "react";
import { useLocation, NavLink } from "react-router-dom";
import PropTypes from "prop-types";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import Breadcrumbs from "common/Breadcrumbs";
import NotificationItem from "common/Items/NotificationItem";
import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarIconButton,
  navbarMobileMenu,
} from "common/Navbars/DashboardNavbar/styles";
import {
  useMaterialUIController,
  setTransparentNavbar,
  setMiniSidenav,
  setOpenConfigurator,
} from "context";
import { Me } from "store/slice/usersSlice";
import { useDispatch, useSelector } from "react-redux";
import MDAvatar from "components/MDAvatar";
import { Box, Divider, MenuItem, MenuList, Popover, Typography } from '@mui/material';

function DashboardNavbar({ absolute, light, isMini }) {
  const userStore = useSelector(store => store.user);
  const user = userStore?.user;

  const [navbarType, setNavbarType] = useState();
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentNavbar, fixedNavbar, openConfigurator, darkMode } = controller;
  const [openMenu, setOpenMenu] = useState(false);
  const [accountMenu, setAccountMenu] = useState(false);
  const route = useLocation().pathname.split("/").slice(1);
  const dispatcher = useDispatch();

  useEffect(() => {
    if (fixedNavbar) {
      setNavbarType("sticky");
    } else {
      setNavbarType("static");
    }

    function handleTransparentNavbar() {
      setTransparentNavbar(dispatch, (fixedNavbar && window.scrollY === 0) || !fixedNavbar);
    }
    window.addEventListener("scroll", handleTransparentNavbar);
    handleTransparentNavbar();

    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);
  const handleOpenMenu = (event) => setOpenMenu(event.currentTarget);
  const handleCloseMenu = () => setOpenMenu(false)
  
  const handleAccountOpenMenu = (event) => setAccountMenu(event.currentTarget);
  const handleAccountCloseMenu = () => setAccountMenu(false);
  const Logout = () => {
    localStorage.removeItem('auth-token')
    dispatcher(Me())
  }

  // Render the notifications menu
  const renderMenu = () => (
    <Menu
      anchorEl={openMenu}
      anchorReference={null}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      open={Boolean(openMenu)}
      onClose={handleCloseMenu}
      sx={{ mt: 2 }}
    >
      <NotificationItem icon={<Icon>email</Icon>} title="Check new messages" />
      <NotificationItem icon={<Icon>podcasts</Icon>} title="Manage Podcast sessions" />
      <NotificationItem icon={<Icon>shopping_cart</Icon>} title="Payment successfully completed" />
    </Menu>
  );  
  
  const renderAccountMenu = () => (
    <Menu
      anchorEl={accountMenu}
      anchorReference={null}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      style={{}}
      open={Boolean(accountMenu)}
      onClose={handleAccountCloseMenu}
      sx={{ mt: 0, p: 0, minWidth: 200 }}
    >
      <Box
        sx={{
          py: 1.5,
          px: 2
        }}
      >
        <Typography variant="overline" >
          Account
        </Typography>
        <Typography
          color="text.secondary"
          variant="body2"
        >
          <MDBox component={NavLink} to="/" display="flex" alignItems="center">{user.name}</MDBox>
        </Typography>
      </Box>
      <Divider style={{marginBottom:5, marginTop: 0}}/>
      <NotificationItem icon={<Icon>logout</Icon>} title="Logout" onClick={Logout}/>
    </Menu>
  );

  // Styles for the navbar icons
  const iconsStyle = ({ palette: { dark, white, text }, functions: { rgba } }) => ({
    color: () => {
      let colorValue = light || darkMode ? white.main : dark.main;

      if (transparentNavbar && !light) {
        colorValue = darkMode ? rgba(text.main, 0.6) : text.main;
      }

      return colorValue;
    },
  });
  const appBarSx = useMemo(
    () => (theme) => navbar(theme, { transparentNavbar, absolute, light, darkMode }),
    [transparentNavbar, absolute, light, darkMode]
  );
  
  return (
    <AppBar
      position={absolute ? "absolute" : navbarType}
      color="inherit"
      sx={appBarSx}
    >
      <Toolbar sx={(theme) => navbarContainer(theme)}>
        <MDBox color="inherit" mb={{ xs: 1, md: 0 }} sx={(theme) => navbarRow(theme, { isMini })}>
          <Breadcrumbs icon="home" title={route[route.length - 1]} route={route} light={light} />
        </MDBox>
        {isMini ? null : (
          <MDBox sx={(theme) => navbarRow(theme, { isMini })}>
        
            <MDBox color={light ? "white" : "inherit"}>
              <IconButton 
                size="medium" 
                disableRipple 
                onClick={handleAccountOpenMenu}
              >
                {user.image ? <MDAvatar src={user.image} alt="profile-image"  shadow="sm" />: <Icon sx={{ ...iconsStyle, fontSize: "2.5rem !important" }}>account_circle</Icon>} 
              </IconButton>
              <IconButton
                size="small"
                disableRipple
                color="inherit"
                sx={navbarMobileMenu}
                onClick={handleMiniSidenav}
              >
                <Icon sx={iconsStyle} fontSize="medium">
                  {miniSidenav ? "menu_open" : "menu"}
                </Icon>
              </IconButton>
             
              {renderMenu()}
              {renderAccountMenu()}
            </MDBox>
          </MDBox>
        )}
      </Toolbar>
    </AppBar>
  );
}

// Setting default values for the props of DashboardNavbar
DashboardNavbar.defaultProps = {
  absolute: false,
  light: false,
  isMini: false,
};

// Typechecking props for the DashboardNavbar
DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
};

export default DashboardNavbar;
